import { useEffect, useRef } from 'react';
import maplibregl, { type LngLatLike, type Map as MLMap, type StyleSpecification, type MapMouseEvent, type LngLat } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { MapLayer, Intake } from '@/types';
import { psh } from '@/api/endpoints';

export type Bbox4326 = [number, number, number, number]; // [minLng, minLat, maxLng, maxLat]

export type MarkerMode = 'intake' | 'boreholes';

/** GeoJSON FeatureCollection w EPSG:4326 do narzucenia jako warstwa wektorowa (np. działki EGIB). */
export interface ParcelsOverlay {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    id: string;
    properties: Record<string, string | number | null | undefined>;
    geometry: { type: 'Polygon' | 'MultiPolygon'; coordinates: unknown };
  }>;
}

/**
 * Jeden wpis stosu warstw (model GIS): warstwa źródłowa + krycie (0..100) + widoczność.
 * Kolejność w tablicy jest GÓRA→DÓŁ — index 0 rysuje się na wierzchu mapy.
 */
export interface StackLayer {
  layer: MapLayer;
  opacity: number;   // 0..100
  visible: boolean;
}

interface Props {
  center?: LngLatLike;
  zoom?: number;
  aktywneLayers?: MapLayer[];
  intake?: Intake | null;
  height?: string;
  drawMode?: boolean;
  bbox?: Bbox4326 | null;
  onBboxChange?: (bbox: Bbox4326 | null) => void;
  markerMode?: MarkerMode;
  /**
   * Każda zmiana wartości (np. inkrementacja licznika) wywołuje przelot mapy do
   * współrzędnych ujęcia. Pierwszy przelot odbywa się raz przy zmianie samego ujęcia,
   * ten parametr daje parentowi sposób na ręczne ponowne centrowanie po klikuracji
   * "Centruj na ujęciu" — bez konieczności przeładowywania komponentu.
   */
  centerTrigger?: number;
  /** Wektorowa warstwa działek EGIB; klik na działkę → popup. null/undefined = warstwa wyłączona. */
  parcelsOverlay?: ParcelsOverlay | null;
  /** Zbiór egibId-ów już zapisanych przy ujęciu — renderowane innym kolorem (zielony zamiast amber). */
  savedParcelIds?: Set<string>;
  /** egibId działki do podświetlenia (klik w wiersz listy) — inny kolor + dolot mapy do jej granic. */
  focusedParcelId?: string | null;
  /**
   * Klik na poligon działki — zamiast pokazywać popup, woła ten callback (jeśli zdefiniowany)
   * z atrybutami klikniętego feature. Używane do toggle "dodaj/usuń z operatu".
   */
  onParcelClick?: (props: Record<string, string | number | null | undefined>) => void;
  /** Okręg-bufor wokół punktu (np. dookoła ujęcia). Renderowany jako wektor poligon. */
  bufferCircle?: { lon: number; lat: number; radiusM: number; color?: string } | null;
  /**
   * Poligon obszaru zasilania / strefy oddziaływania ujęcia (GeoJSON geometry LUB Feature w EPSG:4326,
   * jako string). Renderowany jako wypełniony wektor pod warstwami działek/otworów. Faithful względem
   * geometrii z backendu — obsługuje zarówno liczony okrąg Sichardta, jak i wgrany/narysowany OSW.
   * null = warstwa wyłączona.
   */
  catchmentOverlay?: { geoJson: string; color?: string } | null;
  /**
   * Wgrane granice stref ochronnych ujęcia (F4) — FeatureCollection w EPSG:4326. Bursztynowy,
   * kreskowany obrys nad strefą oddziaływania. null = warstwa wyłączona.
   */
  protectionOverlay?: GeoJSON.FeatureCollection | null;
  /**
   * Warstwa obiektów presji (BDOT10k/MIDAS/CLC…) wykrytych w obszarze zasilania — FeatureCollection
   * w EPSG:4326. Renderowana jako wypełniony wektor w ciepłym kolorze („zagrożenie") pod działkami.
   * null = warstwa wyłączona.
   */
  pressureOverlay?: GeoJSON.FeatureCollection | null;
  /**
   * Tryb rysowania obszaru (poligonu): klik = wierzchołek, podwójny klik = zakończ (≥3 wierzchołki),
   * Esc = anuluj. Po zakończeniu wywołuje {@link onPolygonComplete} z gotowym Polygon (EPSG:4326).
   */
  drawPolygonMode?: boolean;
  onPolygonComplete?: (polygon: GeoJSON.Polygon) => void;
  /** Pokazuj otwory Państwowej Służby Hydrogeologicznej (CBDG) jako osobną warstwę wektorową. */
  showPsh?: boolean;
  /**
   * Uporządkowany stos warstw WMS/XYZ (GÓRA→DÓŁ; index 0 = na wierzchu mapy) z krycie i widocznością.
   * Ma pierwszeństwo przed {@link aktywneLayers}. Gdy podany, silnik pracuje na 3 efektach:
   * strukturalnym (montaż/kolejność), malującym (krycie+widoczność) i markerów — bez migotania kafelków.
   */
  layerStack?: StackLayer[];
  /** Callback pozycji kursora nad mapą (WGS84 / EPSG:4326). null gdy kursor opuścił mapę. */
  onCursorMove?: (c: { lng: number; lat: number } | null) => void;
  /** Ukryte typy otworów PSH (klucze z {@link PSH_TYPES}). Puste/undefined = wszystkie widoczne. */
  pshHiddenTypes?: string[];
  /** Raport liczności typów PSH w bieżącym widoku (klucz typu → liczba) do panelu filtra. */
  onPshStats?: (counts: Record<string, number>) => void;
  /** Stan warstwy PSH — patrz {@link PshState}; pozwala pokazać, DLACZEGO nie widać otworów (uwaga 4). */
  onPshState?: (state: PshState) => void;
  /** Cel wyszukiwarki miejscowości — przelot mapy + tymczasowy znacznik. null = brak. */
  searchTarget?: { lng: number; lat: number; label: string } | null;
  /**
   * Dolot do geometrii wskazanego obiektu (GeoJSON, EPSG:4326) — np. presja wybrana z listy.
   * {@code nonce} wymusza reakcję także przy ponownym wskazaniu tego samego obiektu.
   * Opcjonalny: pozostali konsumenci mapy (/mapy, kreator, karta ujęcia) go nie podają.
   */
  focusGeometry?: { geoJson: string; nonce: number } | null;
  /**
   * Geometria (GeoJSON, EPSG:4326) obiektu WYRÓŻNIONEGO na mapie — utrzymywana dopóki obiekt jest
   * wybrany na liście. Niezależna od {@link focusGeometry}, które tylko wyzwala jednorazowy dolot.
   */
  highlightGeometry?: string | null;
  /** Studnie ujęcia — punkty na mapie z oznaczeniem (dane wejściowe obszaru zasilania). */
  wellPoints?: { designation: string; latitude: number | null; longitude: number | null }[];
  /** Własne zagrożenia wskazane na mapie oceny — punkty z tytułem i kolorem kategorii (klik → popup). */
  hazardPoints?: { id: number; title: string; categoryColor?: string | null; latitude: number | null; longitude: number | null }[];
  /** Tryb wskazywania punktu: kursor krzyżyk, pojedynczy klik → {@link onPointPick} (WGS84). */
  pickPointMode?: boolean;
  onPointPick?: (lng: number, lat: number) => void;
  /** Zestaw kodów warstw, których kafelki są w tej chwili doczytywane (live). Do wskaźnika ładowania. */
  onLayerLoadingChange?: (loadingCodes: Set<string>) => void;
  /**
   * Podgląd dopasowywanej mapy rastrowej PRZED jej zatwierdzeniem (georeferencer). `url` to szablon
   * kafelka WMS-style z `{bbox-epsg-3857}`. Rysowany nad pozostałymi rastrami, ale pod overlayami
   * wektorowymi, żeby granice i punkty były nadal widoczne. null = brak podglądu.
   */
  previewRaster?: { url: string; opacity: number } | null;
}

const PSH_SOURCE = 'psh-boreholes';
const PSH_LAYER = 'psh-boreholes-circle';
/** Poniżej tego zoomu NIE pytamy CBDG — bbox całego województwa zwróciłby tysiące otworów i limit 600
 *  ucinałby je losowo. Próg i komunikat „przybliż" muszą pochodzić z tej samej stałej. */
export const PSH_MIN_ZOOM = 10;

/**
 * Stan warstwy otworów PSH (uwaga 4 z 30.07.2026). Dotąd wszystkie trzy przypadki wyglądały identycznie —
 * pusta mapa — i nie dało się odróżnić „za daleko" od „w tym widoku nie ma otworów" i od awarii usługi.
 */
export type PshState =
  | { status: 'zoom' }
  | { status: 'loading' }
  | { status: 'ok'; count: number }
  | { status: 'empty'; count: number }
  | { status: 'error'; message: string };

const PARCELS_SOURCE = 'parcels-egib';
const PARCELS_FILL = 'parcels-egib-fill';
const PARCELS_LINE = 'parcels-egib-line';

const BBOX_SOURCE = 'draw-bbox';
const BBOX_FILL = 'draw-bbox-fill';
const BBOX_LINE = 'draw-bbox-line';

const BUFFER_SOURCE = 'buffer-circle';
const BUFFER_FILL = 'buffer-circle-fill';
const BUFFER_LINE = 'buffer-circle-line';

const CATCHMENT_SOURCE = 'risk-catchment';
const CATCHMENT_FILL = 'risk-catchment-fill';
const CATCHMENT_LINE = 'risk-catchment-line';

const WELLS_SOURCE = 'risk-wells';
const WELLS_CIRCLE = 'risk-wells-circle';
const WELLS_LABEL = 'risk-wells-label';
const HAZARDS_SOURCE = 'risk-hazards';
const HAZARDS_CIRCLE = 'risk-hazards-circle';
const HAZARDS_LABEL = 'risk-hazards-label';
const PRESSURE_SOURCE = 'risk-pressure';
const PRESSURE_FILL = 'risk-pressure-fill';
const PRESSURE_LINE = 'risk-pressure-line';
const PROTECTION_SOURCE = 'risk-protection';
const PROTECTION_FILL = 'risk-protection-fill';
const PROTECTION_LINE = 'risk-protection-line';

const PRESSURE_SEL_SOURCE = 'risk-pressure-sel';
const PRESSURE_SEL_FILL = 'risk-pressure-sel-fill';
const PRESSURE_SEL_LINE = 'risk-pressure-sel-line';

const DRAWPOLY_SOURCE = 'draw-poly';
const DRAWPOLY_FILL = 'draw-poly-fill';
const DRAWPOLY_LINE = 'draw-poly-line';
const DRAWPOLY_PTS = 'draw-poly-pts';

/** Warstwy wektorowe/overlaye, POD które musi trafić każdy raster WMS (żeby granice/otwory były widoczne). */
const OVERLAYS = [CATCHMENT_FILL, PROTECTION_FILL, PRESSURE_FILL, PRESSURE_SEL_FILL, WELLS_CIRCLE, HAZARDS_CIRCLE, DRAWPOLY_FILL, BBOX_FILL, PARCELS_FILL, BUFFER_FILL, PSH_LAYER];
/** Id najniższego obecnego overlay-a — kotwica dla addLayer/moveLayer WMS. undefined = brak overlayów. */
function overlayAnchor(m: MLMap): string | undefined {
  return (m.getStyle().layers ?? []).map(l => l.id).find(id => OVERLAYS.includes(id));
}

/** Mapy, dla których styl został już sparsowany (bezpieczne addSource/addLayer). */
const stylSparsowany = new WeakSet<MLMap>();

/**
 * Wykonuje `fn`, gdy styl mapy jest gotowy na addSource/addLayer; zwraca funkcję odsubskrybowania.
 *
 * Dlaczego NIE `isStyleLoaded()` i NIE `once('idle')` (zweryfikowane w źródłach maplibre-gl 5.23):
 *  - `isStyleLoaded()` → `Style.loaded()` zwraca false, dopóki KTÓRYKOLWIEK kafel w widoku się
 *    ładuje (TileManager.loaded()), a `'idle'` leci pod jeszcze ostrzejszym `Map.loaded()`;
 *  - tymczasem addSource/addLayer wymagają WYŁĄCZNIE sparsowanego stylu (Style._checkLoaded).
 * Skutek starego wzorca: jeden wiszący kafel (np. WMS przez proxy z martwym upstreamem) blokował
 * OBIE gałęzie i warstwy nie pojawiały się aż do pan/zoom — zgłoszone „czasami nie wszystkie widać
 * na początku". Dlatego bramkujemy na `getStyle()`, które nie zależy od kafli.
 */
function gdyStylGotowy(m: MLMap, fn: () => void): () => void {
  // getStyle() przed sparsowaniem stylu potrafi zwrócić undefined albo rzucić — traktujemy jak „jeszcze nie"
  const stylJest = () => { try { return !!m.getStyle(); } catch { return false; } };
  if (stylSparsowany.has(m) || stylJest()) { stylSparsowany.add(m); fn(); return () => {}; }
  const h = () => {
    if (!stylJest()) return;            // styl wciąż nieparsowany — czekamy na kolejne 'styledata'
    stylSparsowany.add(m);
    m.off('styledata', h);
    fn();
  };
  m.on('styledata', h);
  return () => { m.off('styledata', h); };
}

export interface PshType { key: string; label: string; color: string; }
/** Kanoniczne typy otworów PSH wg pola „Cel_wiercenia" (CBDG). Kolor = kolor kółka na mapie + w legendzie. */
export const PSH_TYPES: PshType[] = [
  { key: 'hydro', label: 'Hydrogeologiczne',         color: '#0891b2' },
  { key: 'zloz',  label: 'Złożowe',                  color: '#a16207' },
  { key: 'inz',   label: 'Geologiczno-inżynierskie', color: '#475569' },
  { key: 'bad',   label: 'Badawcze',                 color: '#7c3aed' },
  { key: 'tech',  label: 'Technologiczne',           color: '#059669' },
  { key: 'kart',  label: 'Kartograficzne',           color: '#db2777' },
  { key: 'inne',  label: 'Inne / brak danych',       color: '#9ca3af' },
];

/** Klasyfikacja surowego „Cel_wiercenia" (purpose) do kanonicznego klucza typu. */
export function classifyPshPurpose(purpose?: string | null): string {
  const p = (purpose ?? '').toLowerCase();
  if (p.includes('hydrogeolog')) return 'hydro';
  if (p.includes('złoż') || p.includes('zloz')) return 'zloz';
  if (p.includes('inżyn') || p.includes('inzyn')) return 'inz';
  if (p.includes('badaw')) return 'bad';
  if (p.includes('technolog')) return 'tech';
  if (p.includes('kartograf')) return 'kart';
  return 'inne';
}

// Kolor kółka PSH wg właściwości `_type` (match → kolor; domyślnie „inne").
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PSH_COLOR_MATCH: any = ['match', ['get', '_type'],
  ...PSH_TYPES.filter(t => t.key !== 'inne').flatMap(t => [t.key, t.color]),
  '#9ca3af',
];

// Filtr warstwy PSH: ukryj typy z `hidden` (pusty → null = brak filtra = wszystko widoczne).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function pshFilterExpr(hidden: string[]): any {
  return hidden && hidden.length ? ['!', ['in', ['get', '_type'], ['literal', hidden]]] : null;
}

/** Buduje approximację koła w EPSG:4326 jako Polygon z 64 segmentami. */
function circleFeature(lon: number, lat: number, radiusM: number): GeoJSON.Feature<GeoJSON.Polygon> {
  const segments = 64;
  const earthR = 6371008.8;
  const angularDist = radiusM / earthR;
  const lat0 = (lat * Math.PI) / 180;
  const lon0 = (lon * Math.PI) / 180;
  const ring: [number, number][] = [];
  for (let i = 0; i <= segments; i++) {
    const bearing = ((i * 360) / segments) * (Math.PI / 180);
    const sinLat = Math.sin(lat0) * Math.cos(angularDist)
                 + Math.cos(lat0) * Math.sin(angularDist) * Math.cos(bearing);
    const newLat = Math.asin(sinLat);
    const newLon = lon0 + Math.atan2(
      Math.sin(bearing) * Math.sin(angularDist) * Math.cos(lat0),
      Math.cos(angularDist) - Math.sin(lat0) * sinLat,
    );
    ring.push([(newLon * 180) / Math.PI, (newLat * 180) / Math.PI]);
  }
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'Polygon', coordinates: [ring] },
  };
}

function bboxFeature(bbox: Bbox4326): GeoJSON.Feature<GeoJSON.Polygon> {
  const [minLng, minLat, maxLng, maxLat] = bbox;
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [minLng, minLat],
        [maxLng, minLat],
        [maxLng, maxLat],
        [minLng, maxLat],
        [minLng, minLat],
      ]],
    },
  };
}

/** Warstwy rysowanego poligonu: wypełnienie (≥3 wierzchołki), linia (z podglądem do kursora), punkty. */
function drawPolyFeatures(vertices: [number, number][], cursor: [number, number] | null): GeoJSON.FeatureCollection {
  const feats: GeoJSON.Feature[] = [];
  const line = cursor ? [...vertices, cursor] : vertices;
  if (line.length >= 2) feats.push({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: line } });
  if (vertices.length >= 3) feats.push({ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [[...vertices, vertices[0]]] } });
  for (const v of vertices) feats.push({ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: v } });
  return { type: 'FeatureCollection', features: feats };
}

function updateDrawPolyLayer(m: MLMap, vertices: [number, number][], cursor: [number, number] | null) {
  const data = drawPolyFeatures(vertices, cursor);
  const src = m.getSource(DRAWPOLY_SOURCE) as maplibregl.GeoJSONSource | undefined;
  if (src) { src.setData(data); return; }
  if (vertices.length === 0) return;
  m.addSource(DRAWPOLY_SOURCE, { type: 'geojson', data });
  const before = m.getLayer(PARCELS_FILL) ? PARCELS_FILL : undefined;
  m.addLayer({ id: DRAWPOLY_FILL, type: 'fill', source: DRAWPOLY_SOURCE, filter: ['==', '$type', 'Polygon'],
    paint: { 'fill-color': '#7c3aed', 'fill-opacity': 0.12 } }, before);
  m.addLayer({ id: DRAWPOLY_LINE, type: 'line', source: DRAWPOLY_SOURCE, filter: ['==', '$type', 'LineString'],
    paint: { 'line-color': '#7c3aed', 'line-width': 2, 'line-dasharray': [2, 1] } }, before);
  m.addLayer({ id: DRAWPOLY_PTS, type: 'circle', source: DRAWPOLY_SOURCE, filter: ['==', '$type', 'Point'],
    paint: { 'circle-radius': 4, 'circle-color': '#ffffff', 'circle-stroke-color': '#7c3aed', 'circle-stroke-width': 2 } }, before);
}

function clearDrawPolyLayer(m: MLMap) {
  [DRAWPOLY_PTS, DRAWPOLY_LINE, DRAWPOLY_FILL].forEach(id => { if (m.getLayer(id)) m.removeLayer(id); });
  if (m.getSource(DRAWPOLY_SOURCE)) m.removeSource(DRAWPOLY_SOURCE);
}

/** Ucieczka encji HTML — tytuł zagrożenia jest wpisywany przez użytkownika, a trafia do popupu przez setHTML. */
function escapeHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

export function MapLibreMap({
  center, zoom = 13, aktywneLayers = [], intake = null, height = '520px',
  drawMode = false, bbox = null, onBboxChange, markerMode = 'intake',
  centerTrigger, parcelsOverlay = null,
  savedParcelIds, focusedParcelId = null, onParcelClick, bufferCircle = null, catchmentOverlay = null, protectionOverlay = null, pressureOverlay = null, showPsh = false,
  drawPolygonMode = false, onPolygonComplete,
  layerStack, onCursorMove, pshHiddenTypes, onPshStats, onPshState, searchTarget = null, focusGeometry = null,
  previewRaster = null,
  highlightGeometry = null, wellPoints = [], hazardPoints = [], pickPointMode = false, onPointPick,
  onLayerLoadingChange,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  const drawModeRef = useRef(drawMode);
  const onBboxChangeRef = useRef(onBboxChange);
  const startLngLatRef = useRef<LngLat | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const searchMarkerRef = useRef<maplibregl.Marker | null>(null);
  const lastFlewIntakeIdRef = useRef<number | null>(null);
  /** Ostatnie `focusGeometry.nonce`, na które mapa już doleciała — deklarowany tu, bo zeruje go
   *  sprzątanie instancji mapy (patrz niżej). */
  const lastFocusNonceRef = useRef<number | null>(null);
  const lastCenterTriggerRef = useRef<number | undefined>(centerTrigger);
  const onParcelClickRef = useRef(onParcelClick);
  const parcelsOverlayRef = useRef(parcelsOverlay);
  parcelsOverlayRef.current = parcelsOverlay;
  const onCursorMoveRef = useRef(onCursorMove);
  const onPshStatsRef = useRef(onPshStats);
  const onPshStateRef = useRef(onPshState);
  const onLayerLoadingChangeRef = useRef(onLayerLoadingChange);
  const pshHiddenRef = useRef<string[]>(pshHiddenTypes ?? []);
  pshHiddenRef.current = pshHiddenTypes ?? [];
  const drawPolyModeRef = useRef(drawPolygonMode);
  const drawVerticesRef = useRef<[number, number][]>([]);
  const onPolygonCompleteRef = useRef(onPolygonComplete);
  onPolygonCompleteRef.current = onPolygonComplete;
  const pickPointModeRef = useRef(pickPointMode);
  const onPointPickRef = useRef(onPointPick);
  onPointPickRef.current = onPointPick;

  useEffect(() => { drawModeRef.current = drawMode; }, [drawMode]);
  useEffect(() => { onBboxChangeRef.current = onBboxChange; }, [onBboxChange]);
  useEffect(() => { onParcelClickRef.current = onParcelClick; }, [onParcelClick]);
  useEffect(() => { onCursorMoveRef.current = onCursorMove; }, [onCursorMove]);
  useEffect(() => { onPshStatsRef.current = onPshStats; }, [onPshStats]);
  useEffect(() => { onPshStateRef.current = onPshState; }, [onPshState]);
  useEffect(() => { onLayerLoadingChangeRef.current = onLayerLoadingChange; }, [onLayerLoadingChange]);

  // Tryb rysowania poligonu: kursor krzyżyk + wyłącz zoom na dblclick; wyjście czyści bufor wierzchołków.
  useEffect(() => {
    drawPolyModeRef.current = drawPolygonMode;
    const m = mapRef.current;
    if (!m) return;
    if (drawPolygonMode) {
      m.getCanvas().style.cursor = 'crosshair';
      m.doubleClickZoom.disable();
    } else {
      m.getCanvas().style.cursor = '';
      m.doubleClickZoom.enable();
      drawVerticesRef.current = [];
      clearDrawPolyLayer(m);
    }
  }, [drawPolygonMode]);

  // Tryb wskazywania punktu (własne zagrożenie): kursor krzyżyk; sam klik obsługuje onPickClick (init).
  useEffect(() => {
    pickPointModeRef.current = pickPointMode;
    const m = mapRef.current;
    if (!m) return;
    m.getCanvas().style.cursor = (pickPointMode || drawPolyModeRef.current || drawModeRef.current) ? 'crosshair' : '';
  }, [pickPointMode]);

  // Efektywny stos warstw: gdy parent podał `layerStack`, używamy go (GÓRA→DÓŁ);
  // inaczej fallback na starą prop `aktywneLayers` (wszystkie widoczne, krycie = defaultOpacity)
  // — dzięki temu strony ujęć/EGIB korzystające ze starego API działają bez zmian.
  const effStack: StackLayer[] = layerStack
    ?? aktywneLayers.map(l => ({ layer: l, opacity: l.defaultOpacity ?? 80, visible: true }));
  const stackRef = useRef(effStack);
  stackRef.current = effStack;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const defaultCenter: LngLatLike = center ?? (intake?.longitude && intake?.latitude
        ? [Number(intake.longitude), Number(intake.latitude)]
        : [19.1451, 51.9194]);

    const style: StyleSpecification = {
      version: 8,
      sources: {
        osm: {
          type: 'raster',
          tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
          tileSize: 256,
          // OSM Standard tile server hostuje tylko do zoom 19 — wyższe zwracają 404
          // bez CORS, MapLibre logguje to jako AJAXError. Z maxzoom: 19 MapLibre
          // oversampluje (re-use z=19) zamiast prosić o nieistniejące kafelki.
          maxzoom: 19,
          attribution: '&copy; OpenStreetMap contributors',
        },
      },
      layers: [
        { id: 'osm', type: 'raster', source: 'osm' },
      ],
    };

    const m = new maplibregl.Map({
      container: containerRef.current,
      style,
      center: defaultCenter,
      zoom,
    });
    m.addControl(new maplibregl.NavigationControl(), 'top-right');
    // ScaleControl na dole-prawo — dół-lewo zajmuje pływająca legenda kompozycji.
    m.addControl(new maplibregl.ScaleControl(), 'bottom-right');
    mapRef.current = m;

    // W układzie flex/grid kontener dostaje wysokość PO inicjalizacji mapy (height:100%),
    // więc MapLibre utknąłby na fallbacku 400×300. ResizeObserver wymusza dopasowanie canvasu.
    const ro = new ResizeObserver(() => m.resize());
    if (containerRef.current) ro.observe(containerRef.current);

    // Drawing handlers — używają refów, więc zostają aktualne bez ponownej rejestracji.
    const onMouseDown = (e: MapMouseEvent) => {
      if (!drawModeRef.current) return;
      e.preventDefault();
      m.dragPan.disable();
      m.boxZoom.disable();
      startLngLatRef.current = e.lngLat;
      const ll = e.lngLat;
      updateBboxLayer(m, [ll.lng, ll.lat, ll.lng, ll.lat]);
    };
    const onMouseMove = (e: MapMouseEvent) => {
      if (!drawModeRef.current || !startLngLatRef.current) return;
      const a = startLngLatRef.current;
      const b = e.lngLat;
      const minLng = Math.min(a.lng, b.lng);
      const maxLng = Math.max(a.lng, b.lng);
      const minLat = Math.min(a.lat, b.lat);
      const maxLat = Math.max(a.lat, b.lat);
      updateBboxLayer(m, [minLng, minLat, maxLng, maxLat]);
    };
    const onMouseUp = (e: MapMouseEvent) => {
      if (!drawModeRef.current || !startLngLatRef.current) return;
      const a = startLngLatRef.current;
      const b = e.lngLat;
      startLngLatRef.current = null;
      m.dragPan.enable();
      m.boxZoom.enable();
      const minLng = Math.min(a.lng, b.lng);
      const maxLng = Math.max(a.lng, b.lng);
      const minLat = Math.min(a.lat, b.lat);
      const maxLat = Math.max(a.lat, b.lat);
      // Ignoruj drobne kliknięcia bez ruchu
      if (Math.abs(maxLng - minLng) < 1e-6 || Math.abs(maxLat - minLat) < 1e-6) {
        updateBboxLayer(m, null);
        onBboxChangeRef.current?.(null);
        return;
      }
      const finalBbox: Bbox4326 = [minLng, minLat, maxLng, maxLat];
      updateBboxLayer(m, finalBbox);
      onBboxChangeRef.current?.(finalBbox);
    };
    // Odczyt współrzędnych kursora (WGS84) — do pływającej plakietki nad mapą.
    const onCursor = (e: MapMouseEvent) => onCursorMoveRef.current?.({ lng: e.lngLat.lng, lat: e.lngLat.lat });
    const onCursorOut = () => onCursorMoveRef.current?.(null);

    // Rysowanie poligonu: klik = wierzchołek, ruch = podgląd „gumka", dblclick = zakończ, Esc = anuluj.
    const near = (a: [number, number], b: [number, number]) => Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9;
    const onDrawClick = (e: MapMouseEvent) => {
      if (!drawPolyModeRef.current) return;
      drawVerticesRef.current.push([e.lngLat.lng, e.lngLat.lat]);
      updateDrawPolyLayer(m, drawVerticesRef.current, null);
    };
    const onDrawMove = (e: MapMouseEvent) => {
      if (!drawPolyModeRef.current || drawVerticesRef.current.length === 0) return;
      updateDrawPolyLayer(m, drawVerticesRef.current, [e.lngLat.lng, e.lngLat.lat]);
    };
    const onDrawDblClick = (e: MapMouseEvent) => {
      if (!drawPolyModeRef.current) return;
      e.preventDefault();
      const v = drawVerticesRef.current;
      while (v.length >= 2 && near(v[v.length - 1], v[v.length - 2])) v.pop(); // dblclick dodał duplikaty
      if (v.length < 3) return;
      const poly: GeoJSON.Polygon = { type: 'Polygon', coordinates: [[...v, v[0]]] };
      drawVerticesRef.current = [];
      clearDrawPolyLayer(m);
      onPolygonCompleteRef.current?.(poly);
    };
    const onDrawKey = (ev: KeyboardEvent) => {
      if (drawPolyModeRef.current && ev.key === 'Escape') { drawVerticesRef.current = []; clearDrawPolyLayer(m); }
    };

    // Wskazanie punktu (własne zagrożenie): pojedynczy klik → callback z WGS84. Niezależne od rysowania poligonu.
    const onPickClick = (e: MapMouseEvent) => {
      if (!pickPointModeRef.current) return;
      onPointPickRef.current?.(e.lngLat.lng, e.lngLat.lat);
    };

    // Popup własnego zagrożenia (klik w punkt) — rejestrowany warstwowo; MapLibre deleguje także gdy
    // warstwa dojdzie później. Tytuł ESCAPE'owany (dane od użytkownika) — bez wstrzyknięcia HTML.
    const onHazardClick = (e: MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {
      if (pickPointModeRef.current) return;   // w trybie wskazywania klik zakłada punkt, nie otwiera popupu
      const f = e.features?.[0];
      if (!f) return;
      const p = (f.properties || {}) as Record<string, string | number | null>;
      const html = `<div style="font:12px system-ui">`
        + `<div style="font-weight:700;font-size:14px">${escapeHtml(String(p['title'] ?? 'Zagrożenie'))}</div>`
        + `<div style="margin-top:4px;color:#888;font-size:11px">Zagrożenie wskazane na mapie oceny</div>`
        + `</div>`;
      new maplibregl.Popup({ closeOnClick: true, maxWidth: '260px' }).setLngLat(e.lngLat).setHTML(html).addTo(m);
    };
    const onHazardEnter = () => { if (!pickPointModeRef.current) m.getCanvas().style.cursor = 'pointer'; };
    const onHazardLeave = () => {
      if (!drawModeRef.current && !drawPolyModeRef.current && !pickPointModeRef.current) m.getCanvas().style.cursor = '';
    };

    m.on('mousedown', onMouseDown);
    m.on('mousemove', onMouseMove);
    m.on('mouseup', onMouseUp);
    m.on('mouseout', onMouseUp);
    m.on('mousemove', onCursor);
    m.on('mouseout', onCursorOut);
    m.on('click', onDrawClick);
    m.on('click', onPickClick);
    m.on('click', HAZARDS_CIRCLE, onHazardClick);
    m.on('mouseenter', HAZARDS_CIRCLE, onHazardEnter);
    m.on('mouseleave', HAZARDS_CIRCLE, onHazardLeave);
    m.on('dblclick', onDrawDblClick);
    m.on('mousemove', onDrawMove);
    window.addEventListener('keydown', onDrawKey);

    // Live-wskaźnik ładowania kafelków per warstwa WMS/WMTS/XYZ. Licznik żądań w locie na źródło
    // (source id = 'wms-<code>'): +1 na starcie kafelka, -1 na zakończeniu/błędzie; 'idle' zeruje
    // (bezpiecznik na porzucone żądania po zoom/pan). Proxy zwraca 200 (przezroczysty kafelek) także
    // przy błędzie upstream, więc licznik i tak się domyka.
    const inflight = new Map<string, number>();
    const codeOf = (sid?: string) => (sid && sid.startsWith('wms-') ? sid.slice(4) : null);
    const emitLoading = () => onLayerLoadingChangeRef.current?.(
      new Set([...inflight].filter(([, n]) => n > 0).map(([c]) => c)));
    const onTileStart = (e: maplibregl.MapSourceDataEvent) => {
      const c = e.dataType === 'source' && e.tile ? codeOf(e.sourceId) : null;
      if (!c) return;
      inflight.set(c, (inflight.get(c) ?? 0) + 1);
      emitLoading();
    };
    const onTileEnd = (e: maplibregl.MapSourceDataEvent) => {
      const c = e.dataType === 'source' && e.tile ? codeOf(e.sourceId) : null;
      if (!c) return;
      inflight.set(c, Math.max(0, (inflight.get(c) ?? 0) - 1));
      emitLoading();
    };
    const onMapIdle = () => {
      let changed = false;
      inflight.forEach((n, c) => { if (n !== 0) { inflight.set(c, 0); changed = true; } });
      if (changed) emitLoading();
    };
    m.on('dataloading', onTileStart);
    m.on('data', onTileEnd);
    m.on('error', onTileEnd);
    m.on('idle', onMapIdle);

    return () => {
      ro.disconnect();
      m.off('mousedown', onMouseDown);
      m.off('mousemove', onMouseMove);
      m.off('mouseup', onMouseUp);
      m.off('mouseout', onMouseUp);
      m.off('mousemove', onCursor);
      m.off('mouseout', onCursorOut);
      m.off('click', onDrawClick);
      m.off('click', onPickClick);
      m.off('click', HAZARDS_CIRCLE, onHazardClick);
      m.off('mouseenter', HAZARDS_CIRCLE, onHazardEnter);
      m.off('mouseleave', HAZARDS_CIRCLE, onHazardLeave);
      m.off('dblclick', onDrawDblClick);
      m.off('mousemove', onDrawMove);
      window.removeEventListener('keydown', onDrawKey);
      m.off('dataloading', onTileStart);
      m.off('data', onTileEnd);
      m.off('error', onTileEnd);
      m.off('idle', onMapIdle);
      m.remove();
      mapRef.current = null;
      // Strażniki „już to zrobiłem" są związane z KONKRETNĄ instancją mapy, a nie z komponentem.
      // Bez wyzerowania nowa instancja dziedziczy je i jednorazowe dolot/centrowanie już nie
      // zadziała — widać to w StrictMode (dev montuje efekty dwa razy: pierwszy dolot leci do mapy,
      // która zaraz potem jest niszczona, a druga instancja zostaje na domyślnym środku Polski).
      lastFocusNonceRef.current = null;
      lastFlewIntakeIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cursor + dragPan w trybie rysowania
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    m.getCanvas().style.cursor = drawMode ? 'crosshair' : '';
  }, [drawMode]);

  // Renderuj bbox kontrolowany przez parenta
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const apply = () => updateBboxLayer(m, bbox);
    return gdyStylGotowy(m, apply);
  }, [bbox]);

  // Renderuj wektorową warstwę działek (EGIB).
  // - Każda fiszka ma `_saved: true|false` w properties (na podstawie `savedParcelIds`),
  //   żeby paint mógł rozróżnić kolor: zielony dla zapisanych, amber dla po prostu znalezionych.
  // - Klik: jeśli parent dał `onParcelClick`, woła go (toggle save/unsave); inaczej pokazuje popup.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const apply = () => {
      const existing = m.getSource(PARCELS_SOURCE);
      if (!parcelsOverlay) {
        if (m.getLayer(PARCELS_LINE)) m.removeLayer(PARCELS_LINE);
        if (m.getLayer(PARCELS_FILL)) m.removeLayer(PARCELS_FILL);
        if (existing) m.removeSource(PARCELS_SOURCE);
        return;
      }
      // Wzbogacamy properties o flagę _saved
      const savedSet = savedParcelIds ?? new Set<string>();
      const data: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: parcelsOverlay.features.map(f => ({
          type: 'Feature',
          id: f.id,
          properties: { ...f.properties, _saved: savedSet.has(f.id), _focused: focusedParcelId != null && f.id === focusedParcelId },
          geometry: f.geometry as GeoJSON.Geometry,
        })),
      };
      if (existing && 'setData' in existing) {
        (existing as maplibregl.GeoJSONSource).setData(data);
        return;
      }
      m.addSource(PARCELS_SOURCE, { type: 'geojson', data, generateId: false });
      m.addLayer({
        id: PARCELS_FILL,
        type: 'fill',
        source: PARCELS_SOURCE,
        paint: {
          'fill-color': ['case',
            ['==', ['get', '_focused'], true], '#2563eb', // niebieski dla podświetlonej (klik w liście)
            ['==', ['get', '_saved'], true], '#16a34a',   // zielony dla zapisanych
            '#f59e0b',                                     // amber dla nowych w buforze
          ],
          'fill-opacity': ['case',
            ['==', ['get', '_focused'], true], 0.45,
            ['==', ['get', '_saved'], true], 0.32,
            0.18,
          ],
        },
      });
      m.addLayer({
        id: PARCELS_LINE,
        type: 'line',
        source: PARCELS_SOURCE,
        paint: {
          'line-color': ['case',
            ['==', ['get', '_focused'], true], '#1d4ed8',
            ['==', ['get', '_saved'], true], '#15803d',
            '#92400e',
          ],
          'line-width': ['case',
            ['==', ['get', '_focused'], true], 4,
            ['==', ['get', '_saved'], true], 2.0,
            1.4,
          ],
          'line-opacity': 0.9,
        },
      });
      // Klik: toggle (jeśli onParcelClick) albo popup
      m.on('click', PARCELS_FILL, (e) => {
        const feat = e.features?.[0];
        if (!feat) return;
        const p = (feat.properties || {}) as Record<string, string | number | null>;
        if (onParcelClickRef.current) {
          onParcelClickRef.current(p);
          return;
        }
        const nrDz = p['number'] ?? '—';
        const obreb = p['obrebNazwa'] ? `obręb ${p['obrebNazwa']} (${p['obrebNumer'] ?? ''})` : '';
        const gmina = p['gminaNazwa'] ?? '';
        const pole = p['poleEwidencyjne'];
        const poleStr = pole ? `Powierzchnia: ${pole} m²` : '';
        const id = p['id'] ?? '';
        const html = `<div style="font: 12px system-ui">`
          + `<div style="font-weight:700;font-size:14px">Działka ${nrDz}</div>`
          + (obreb ? `<div>${obreb}</div>` : '')
          + (gmina ? `<div>${gmina}</div>` : '')
          + (poleStr ? `<div>${poleStr}</div>` : '')
          + `<div style="font-family:monospace;color:#666;margin-top:4px">${id}</div>`
          + `</div>`;
        new maplibregl.Popup({ closeOnClick: true, maxWidth: '320px' })
          .setLngLat(e.lngLat)
          .setHTML(html)
          .addTo(m);
      });
      m.on('mouseenter', PARCELS_FILL, () => { m.getCanvas().style.cursor = 'pointer'; });
      m.on('mouseleave', PARCELS_FILL, () => {
        if (!drawModeRef.current) m.getCanvas().style.cursor = '';
      });
    };
    return gdyStylGotowy(m, apply);
  }, [parcelsOverlay, savedParcelIds, focusedParcelId]);

  // Podświetlona działka (klik w wiersz listy) → dolot mapy do jej granic.
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !focusedParcelId) return;
    const feat = parcelsOverlayRef.current?.features.find(f => f.id === focusedParcelId);
    const geom = feat?.geometry as { coordinates?: unknown } | undefined;
    if (!geom?.coordinates) return;
    let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
    const walk = (a: unknown): void => {
      if (!Array.isArray(a)) return;
      if (typeof a[0] === 'number' && typeof a[1] === 'number') {
        const lon = a[0] as number, lat = a[1] as number;
        if (lon < minLon) minLon = lon;
        if (lat < minLat) minLat = lat;
        if (lon > maxLon) maxLon = lon;
        if (lat > maxLat) maxLat = lat;
      } else {
        (a as unknown[]).forEach(walk);
      }
    };
    walk(geom.coordinates);
    if (Number.isFinite(minLon)) {
      m.fitBounds([[minLon, minLat], [maxLon, maxLat]], { padding: 80, maxZoom: 18, duration: 600, essential: true });
    }
  }, [focusedParcelId]);

  // Otwory PSH (CBDG PIG-PIB) — warstwa wektorowa ładowana wg bieżącego bbox (moveend).
  // Odróżniona od otworów własnych (niebieskie markery-romby): bursztynowe kółka + klik → karta.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    // Strażnik wyścigu: żądanie PSH w locie może się rozwiązać PO wyłączeniu warstwy (cleanup),
    // wtedy ensureLayer dodałby osieroconą warstwę. Anulujemy takie spóźnione odpowiedzi.
    let cancelled = false;

    const onClick = (e: MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {
      const f = e.features?.[0];
      if (!f) return;
      const p = (f.properties || {}) as Record<string, string | number | null>;
      const depth = p['depthM'] != null ? `${p['depthM']} m` : '—';
      const card = p['cardUrl'];
      const html = `<div style="font:12px system-ui">`
        + `<div style="font-weight:700;font-size:14px">${p['name'] ?? 'Otwór PSH'}</div>`
        + `<div>Głębokość: ${depth}${p['year'] ? ` · ${p['year']}` : ''}</div>`
        + (p['purpose'] ? `<div>${p['purpose']}</div>` : '')
        + (p['town'] ? `<div style="color:#666">${p['town']}</div>` : '')
        + `<div style="margin-top:5px;color:#888;font-size:11px">PSH / CBDG PIG-PIB</div>`
        + (card ? `<a href="${card}" target="_blank" rel="noopener" style="display:inline-block;margin-top:6px;color:#b45309;font-weight:600">Otwórz kartę otworu ↗</a>` : '')
        + `</div>`;
      new maplibregl.Popup({ closeOnClick: true, maxWidth: '300px' })
        .setLngLat(e.lngLat).setHTML(html).addTo(m);
    };
    const onEnter = () => { m.getCanvas().style.cursor = 'pointer'; };
    const onLeave = () => { if (!drawModeRef.current) m.getCanvas().style.cursor = ''; };

    const ensureLayer = (data: GeoJSON.FeatureCollection) => {
      const src = m.getSource(PSH_SOURCE) as maplibregl.GeoJSONSource | undefined;
      if (src) { src.setData(data); return; }
      m.addSource(PSH_SOURCE, { type: 'geojson', data });
      m.addLayer({
        id: PSH_LAYER, type: 'circle', source: PSH_SOURCE,
        filter: pshFilterExpr(pshHiddenRef.current) || undefined,   // start z bieżącym filtrem typów
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 3.5, 16, 6.5],
          'circle-color': PSH_COLOR_MATCH,      // kolor wg typu (Cel_wiercenia → _type)
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1.4,
          'circle-opacity': 0.92,
        },
      });
    };

    const loadPsh = () => {
      if (m.getZoom() < PSH_MIN_ZOOM) {
        ensureLayer({ type: 'FeatureCollection', features: [] });
        onPshStatsRef.current?.({});
        // uwaga 4 (30.07.2026): do tej pory warstwa po prostu milczała i nie dało się odróżnić
        // „za daleko" od „brak otworów" i od „usługa nie odpowiada"
        onPshStateRef.current?.({ status: 'zoom' });
        return;
      }
      const b = m.getBounds();
      const bbox = `${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`;
      onPshStateRef.current?.({ status: 'loading' });
      psh.boreholes(bbox)
        .then(fc => {
          if (cancelled || !showPsh) return;
          const raw = fc as unknown as GeoJSON.FeatureCollection;
          // Wzbogać każdy otwór o kanoniczny `_type` (do koloru i filtra) + policz typy.
          const counts: Record<string, number> = {};
          for (const f of raw.features) {
            const props = (f.properties ?? {}) as Record<string, unknown>;
            const key = classifyPshPurpose(props['purpose'] as string | null | undefined);
            props['_type'] = key;
            f.properties = props;
            counts[key] = (counts[key] ?? 0) + 1;
          }
          onPshStatsRef.current?.(counts);
          onPshStateRef.current?.({ status: raw.features.length ? 'ok' : 'empty', count: raw.features.length });
          ensureLayer(raw);
        })
        .catch((e: unknown) => {
          // nadal nonfatal dla mapy, ale NIE po cichu — użytkownik ma wiedzieć, że warstwa nie wczytała się
          // przez usługę, a nie dlatego, że w okolicy nie ma otworów
          if (cancelled || !showPsh) return;
          onPshStateRef.current?.({ status: 'error', message: e instanceof Error ? e.message : String(e) });
        });
    };

    const removeAll = () => {
      if (m.getLayer(PSH_LAYER)) m.removeLayer(PSH_LAYER);
      if (m.getSource(PSH_SOURCE)) m.removeSource(PSH_SOURCE);
    };

    const setup = () => {
      if (!showPsh) { removeAll(); return; }
      m.on('click', PSH_LAYER, onClick);
      m.on('mouseenter', PSH_LAYER, onEnter);
      m.on('mouseleave', PSH_LAYER, onLeave);
      m.on('moveend', loadPsh);
      loadPsh();
    };
    // Bramka na sparsowany styl, nie na dociągnięte kafle — patrz javadoc gdyStylGotowy.
    const odsub = gdyStylGotowy(m, setup);

    return () => {
      cancelled = true;
      odsub();
      m.off('moveend', loadPsh);
      m.off('click', PSH_LAYER, onClick);
      m.off('mouseenter', PSH_LAYER, onEnter);
      m.off('mouseleave', PSH_LAYER, onLeave);
      if (showPsh) removeAll();
    };
  }, [showPsh]);

  // Filtr typów PSH na żywo — setFilter na istniejącej warstwie, bez ponownego pobierania danych.
  const pshHiddenSig = (pshHiddenTypes ?? []).join(',');
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const apply = () => { if (m.getLayer(PSH_LAYER)) m.setFilter(PSH_LAYER, pshFilterExpr(pshHiddenRef.current)); };
    return gdyStylGotowy(m, apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pshHiddenSig]);

  // Bufor-okrąg wokół punktu (np. ujęcia) — wektor renderowany jako Polygon.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const apply = () => {
      const existing = m.getSource(BUFFER_SOURCE);
      if (!bufferCircle) {
        if (m.getLayer(BUFFER_LINE)) m.removeLayer(BUFFER_LINE);
        if (m.getLayer(BUFFER_FILL)) m.removeLayer(BUFFER_FILL);
        if (existing) m.removeSource(BUFFER_SOURCE);
        return;
      }
      const feat = circleFeature(bufferCircle.lon, bufferCircle.lat, bufferCircle.radiusM);
      const color = bufferCircle.color ?? '#06B6D4';
      if (existing && 'setData' in existing) {
        (existing as maplibregl.GeoJSONSource).setData(feat);
        return;
      }
      m.addSource(BUFFER_SOURCE, { type: 'geojson', data: feat });
      // Wstawiamy POD warstwę działek żeby polygony były czytelne
      const beforeId = m.getLayer(PARCELS_FILL) ? PARCELS_FILL : undefined;
      m.addLayer({
        id: BUFFER_FILL,
        type: 'fill',
        source: BUFFER_SOURCE,
        paint: { 'fill-color': color, 'fill-opacity': 0.06 },
      }, beforeId);
      m.addLayer({
        id: BUFFER_LINE,
        type: 'line',
        source: BUFFER_SOURCE,
        paint: { 'line-color': color, 'line-width': 2, 'line-dasharray': [4, 3], 'line-opacity': 0.85 },
      }, beforeId);
    };
    return gdyStylGotowy(m, apply);
  }, [bufferCircle]);

  // Obszar zasilania / strefa oddziaływania — poligon z backendu (geometria lub Feature) renderowany
  // jako wypełniony wektor. Wprost odwzorowuje geometrię (okrąg Sichardta / wgrany / narysowany OSW).
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const apply = () => {
      const existing = m.getSource(CATCHMENT_SOURCE);
      if (!catchmentOverlay) {
        if (m.getLayer(CATCHMENT_LINE)) m.removeLayer(CATCHMENT_LINE);
        if (m.getLayer(CATCHMENT_FILL)) m.removeLayer(CATCHMENT_FILL);
        if (existing) m.removeSource(CATCHMENT_SOURCE);
        return;
      }
      let parsed: GeoJSON.Feature | GeoJSON.Geometry;
      try { parsed = JSON.parse(catchmentOverlay.geoJson); } catch { return; }
      // Backend zwraca gołą geometrię (ST_AsGeoJSON) — owiń w Feature; ale przyjmij też gotowy Feature.
      const data: GeoJSON.Feature = parsed && (parsed as GeoJSON.Feature).type === 'Feature'
        ? (parsed as GeoJSON.Feature)
        : { type: 'Feature', properties: {}, geometry: parsed as GeoJSON.Geometry };
      const color = catchmentOverlay.color ?? '#0e7490';
      if (existing && 'setData' in existing) {
        (existing as maplibregl.GeoJSONSource).setData(data);
        return;
      }
      m.addSource(CATCHMENT_SOURCE, { type: 'geojson', data });
      // Pod strefy ochronne/presje/działki/bufor — strefa oddziaływania to najniższy overlay oceny.
      const beforeId = m.getLayer(PROTECTION_FILL) ? PROTECTION_FILL
        : (m.getLayer(PRESSURE_FILL) ? PRESSURE_FILL
          : (m.getLayer(PARCELS_FILL) ? PARCELS_FILL : (m.getLayer(BUFFER_FILL) ? BUFFER_FILL : undefined)));
      m.addLayer({
        id: CATCHMENT_FILL,
        type: 'fill',
        source: CATCHMENT_SOURCE,
        paint: { 'fill-color': color, 'fill-opacity': 0.12 },
      }, beforeId);
      m.addLayer({
        id: CATCHMENT_LINE,
        type: 'line',
        source: CATCHMENT_SOURCE,
        paint: { 'line-color': color, 'line-width': 2, 'line-opacity': 0.9 },
      }, beforeId);
    };
    return gdyStylGotowy(m, apply);
  }, [catchmentOverlay]);

  // F4: granice stref ochronnych (import aktu wojewody) — bursztynowy, kreskowany obrys.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const apply = () => {
      const existing = m.getSource(PROTECTION_SOURCE);
      if (!protectionOverlay || protectionOverlay.features.length === 0) {
        if (m.getLayer(PROTECTION_LINE)) m.removeLayer(PROTECTION_LINE);
        if (m.getLayer(PROTECTION_FILL)) m.removeLayer(PROTECTION_FILL);
        if (existing) m.removeSource(PROTECTION_SOURCE);
        return;
      }
      if (existing && 'setData' in existing) {
        (existing as maplibregl.GeoJSONSource).setData(protectionOverlay);
        return;
      }
      m.addSource(PROTECTION_SOURCE, { type: 'geojson', data: protectionOverlay });
      const beforeId = m.getLayer(PRESSURE_FILL) ? PRESSURE_FILL
        : (m.getLayer(PARCELS_FILL) ? PARCELS_FILL : (m.getLayer(BUFFER_FILL) ? BUFFER_FILL : undefined));
      m.addLayer({
        id: PROTECTION_FILL,
        type: 'fill',
        source: PROTECTION_SOURCE,
        paint: { 'fill-color': '#d97706', 'fill-opacity': 0.07 },
      }, beforeId);
      m.addLayer({
        id: PROTECTION_LINE,
        type: 'line',
        source: PROTECTION_SOURCE,
        paint: { 'line-color': '#b45309', 'line-width': 2, 'line-opacity': 0.9, 'line-dasharray': [2, 2] },
      }, beforeId);
    };
    return gdyStylGotowy(m, apply);
  }, [protectionOverlay]);

  // Warstwa presji (obiekty wykryte w overlay obszaru zasilania) — wypełniony wektor w kolorze „zagrożenia".
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const apply = () => {
      const existing = m.getSource(PRESSURE_SOURCE);
      if (!pressureOverlay || pressureOverlay.features.length === 0) {
        if (m.getLayer(PRESSURE_LINE)) m.removeLayer(PRESSURE_LINE);
        if (m.getLayer(PRESSURE_FILL)) m.removeLayer(PRESSURE_FILL);
        if (existing) m.removeSource(PRESSURE_SOURCE);
        return;
      }
      if (existing && 'setData' in existing) {
        (existing as maplibregl.GeoJSONSource).setData(pressureOverlay);
        return;
      }
      m.addSource(PRESSURE_SOURCE, { type: 'geojson', data: pressureOverlay });
      // Nad strefą zasilania, ale pod działkami/otworami.
      const beforeId = m.getLayer(PARCELS_FILL) ? PARCELS_FILL : (m.getLayer(BUFFER_FILL) ? BUFFER_FILL : undefined);
      m.addLayer({
        id: PRESSURE_FILL,
        type: 'fill',
        source: PRESSURE_SOURCE,
        paint: { 'fill-color': '#ef4444', 'fill-opacity': 0.18 },
      }, beforeId);
      m.addLayer({
        id: PRESSURE_LINE,
        type: 'line',
        source: PRESSURE_SOURCE,
        paint: { 'line-color': '#b91c1c', 'line-width': 1.4, 'line-opacity': 0.9 },
      }, beforeId);
    };
    return gdyStylGotowy(m, apply);
  }, [pressureOverlay]);

  // Studnie ujęcia — punkty z oznaczeniem (dane wejściowe obszaru zasilania art. 4f). Ujęcie to
  // chmura studni, nie punkt technologiczny, więc pokazujemy każdą studnię osobno z jej symbolem.
  const wellsSig = (wellPoints ?? [])
    .map(w => `${w.designation}:${w.latitude},${w.longitude}`).join('|');
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const apply = () => {
      const fc: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: (wellPoints ?? [])
          .filter(w => w.latitude != null && w.longitude != null)
          .map(w => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [Number(w.longitude), Number(w.latitude)] },
            properties: { designation: w.designation },
          })),
      };
      const src = m.getSource(WELLS_SOURCE) as maplibregl.GeoJSONSource | undefined;
      if (src) { src.setData(fc); return; }
      m.addSource(WELLS_SOURCE, { type: 'geojson', data: fc });
      m.addLayer({
        id: WELLS_CIRCLE, type: 'circle', source: WELLS_SOURCE,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 4, 16, 7],
          'circle-color': '#0e7490',            // cyjan wody — odróżnia od presji (czerwień) i PSH
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1.6,
        },
      });
      m.addLayer({
        id: WELLS_LABEL, type: 'symbol', source: WELLS_SOURCE,
        layout: {
          'text-field': ['get', 'designation'],
          'text-size': 11,
          'text-offset': [0, 1.1],
          'text-anchor': 'top',
          'text-allow-overlap': false,
        },
        paint: { 'text-color': '#0e7490', 'text-halo-color': '#ffffff', 'text-halo-width': 1.4 },
      });
    };
    return gdyStylGotowy(m, apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wellsSig]);

  // Własne zagrożenia wskazane na mapie — punkty w kolorze KATEGORII z etykietą tytułu (klik → popup,
  // handlery zarejestrowane raz w init). Renderowane na wierzchu overlayów oceny; kropla ≠ studnia (cyjan).
  const hazardsSig = (hazardPoints ?? [])
    .map(h => `${h.id}:${h.latitude},${h.longitude}:${h.categoryColor ?? ''}:${h.title}`).join('|');
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const apply = () => {
      const fc: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: (hazardPoints ?? [])
          .filter(h => h.latitude != null && h.longitude != null)
          .map(h => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [Number(h.longitude), Number(h.latitude)] },
            properties: { id: h.id, title: h.title, color: h.categoryColor ?? '#dc2626' },
          })),
      };
      const src = m.getSource(HAZARDS_SOURCE) as maplibregl.GeoJSONSource | undefined;
      if (src) { src.setData(fc); return; }
      m.addSource(HAZARDS_SOURCE, { type: 'geojson', data: fc });
      m.addLayer({
        id: HAZARDS_CIRCLE, type: 'circle', source: HAZARDS_SOURCE,
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 5, 16, 9],
          'circle-color': ['coalesce', ['get', 'color'], '#dc2626'],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2.2,
        },
      });
      m.addLayer({
        id: HAZARDS_LABEL, type: 'symbol', source: HAZARDS_SOURCE,
        layout: {
          'text-field': ['get', 'title'],
          'text-size': 11,
          'text-offset': [0, 1.2],
          'text-anchor': 'top',
          'text-allow-overlap': false,
        },
        paint: { 'text-color': '#b91c1c', 'text-halo-color': '#ffffff', 'text-halo-width': 1.4 },
      });
    };
    return gdyStylGotowy(m, apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hazardsSig]);

  // Podświetlenie WYBRANEGO obiektu presji — bursztynowy obrys nad zwykłą warstwą presji, żeby
  // po kliknięciu „Na mapie" było widać KTÓRY obiekt, a nie tylko okolicę. Utrzymywane dopóki
  // obiekt jest wybrany na liście (osobne od jednorazowego dolotu z focusGeometry).
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const apply = () => {
      const usun = () => {
        if (m.getLayer(PRESSURE_SEL_LINE)) m.removeLayer(PRESSURE_SEL_LINE);
        if (m.getLayer(PRESSURE_SEL_FILL)) m.removeLayer(PRESSURE_SEL_FILL);
        if (m.getSource(PRESSURE_SEL_SOURCE)) m.removeSource(PRESSURE_SEL_SOURCE);
      };
      if (!highlightGeometry) { usun(); return; }
      let geom: GeoJSON.Geometry;
      try {
        geom = JSON.parse(highlightGeometry) as GeoJSON.Geometry;
      } catch {
        usun();
        return;
      }
      const dane = { type: 'Feature', properties: {}, geometry: geom } as GeoJSON.Feature;
      const zrodlo = m.getSource(PRESSURE_SEL_SOURCE);
      if (zrodlo && 'setData' in zrodlo) {
        (zrodlo as maplibregl.GeoJSONSource).setData(dane);
        return;
      }
      m.addSource(PRESSURE_SEL_SOURCE, { type: 'geojson', data: dane });
      m.addLayer({
        id: PRESSURE_SEL_FILL,
        type: 'fill',
        source: PRESSURE_SEL_SOURCE,
        paint: { 'fill-color': '#f59e0b', 'fill-opacity': 0.35 },
      });
      m.addLayer({
        id: PRESSURE_SEL_LINE,
        type: 'line',
        source: PRESSURE_SEL_SOURCE,
        // gruby obrys: obiekty liniowe (drogi, koleje) mają w warstwie presji tylko 1,4 px
        paint: { 'line-color': '#b45309', 'line-width': 3.5, 'line-opacity': 1 },
      });
    };
    return gdyStylGotowy(m, apply);
  }, [highlightGeometry]);

  // ── Silnik warstw WMS/XYZ — 3 rozdzielone efekty (bez migotania kafelków) ──

  // (a) STRUKTURALNY: montaż/demontaż warstw + kolejność rysowania. Klucz = sygnatura kodów
  // (orderSig) + obecność overlayów (overlaySig). NIE reaguje na zmiany krycia/widoczności.
  const orderSig = effStack.map(s => s.layer.code).join('|');
  const overlaySig = `${!!bbox}|${!!parcelsOverlay}|${showPsh}|${!!bufferCircle}|${!!catchmentOverlay}|${!!protectionOverlay}|${!!pressureOverlay}|${!!highlightGeometry}|${(wellPoints ?? []).length}|${(hazardPoints ?? []).length}`;
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const apply = () => {
      const stack = stackRef.current;
      const want = new Set(stack.map(s => 'wms-' + s.layer.code));
      // Usuń warstwy/źródła nieobecne już w stosie
      (m.getStyle().layers ?? []).forEach(l => { if (l.id.startsWith('wms-') && !want.has(l.id)) m.removeLayer(l.id); });
      Object.keys(m.getStyle().sources ?? {}).forEach(k => { if (k.startsWith('wms-') && !want.has(k)) m.removeSource(k); });
      const anchor = overlayAnchor(m);
      // Zamontuj brakujące. Krycie/widoczność ustawiamy od razu w addLayer → brak błysku domyślnego krycia.
      stack.forEach(s => {
        const id = 'wms-' + s.layer.code;
        if (m.getLayer(id)) return; // już zamontowana → zostaw (natychmiastowy ponowny pokaz z cache)
        const w = s.layer;
        const proto = (w.protocol ?? '').toUpperCase();
        const isXyz = proto === 'XYZ';
        const isWmts = proto === 'WMTS';
        // XYZ (OSM, OpenTopoMap) — direct URL z {z}/{x}/{y}, brak CORS issue.
        // WMTS — kafelki GetTile przez backend proxy; TILEMATRIXSET=EPSG:3857 (GoogleMapsCompatible)
        //        odpowiada slippy-map z/x/y → TILEMATRIX/TILEROW/TILECOL. Proxy dokleja apikey GUGiK.
        // WMS  — GetMap z bbox przez backend proxy (polskie WMSy nie wystawiają CORS).
        const tms = (w.tileMatrixSet && w.tileMatrixSet.trim()) ? w.tileMatrixSet : 'EPSG:3857';
        const wmtsStyle = (w.wmtsStyle && w.wmtsStyle.trim()) ? w.wmtsStyle : 'default';
        const url = isXyz
          ? (w.url ?? '')
          : isWmts
            ? `/api/wms-proxy/${encodeURIComponent(w.code)}?SERVICE=WMTS&VERSION=1.0.0&REQUEST=GetTile&LAYER=${encodeURIComponent(w.layerId ?? '')}&STYLE=${encodeURIComponent(wmtsStyle)}&FORMAT=${encodeURIComponent(w.imageFormat ?? 'image/png')}&TILEMATRIXSET=${encodeURIComponent(tms)}&TILEMATRIX=${encodeURIComponent(tms)}:{z}&TILEROW={y}&TILECOL={x}`
            : `/api/wms-proxy/${encodeURIComponent(w.code)}?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&CRS=EPSG:3857&WIDTH=256&HEIGHT=256&LAYERS=${encodeURIComponent(w.layerId ?? '')}&STYLES=&FORMAT=${encodeURIComponent(w.imageFormat ?? 'image/png')}&TRANSPARENT=TRUE&BBOX={bbox-epsg-3857}`;
        try {
          // WMTS: cap maxzoom (jak baza OSM) — bez tego MapLibre żąda TILEMATRIX głębszych niż zestaw
          // macierzy, upstream 400 → przezroczysty kafelek; z capem nadpróbkujemy najgłębszy poziom.
          if (!m.getSource(id)) m.addSource(id, { type: 'raster', tiles: [url], tileSize: 256,
            attribution: w.source, ...(isWmts ? { maxzoom: 19 } : {}) });
          m.addLayer({
            id,
            type: 'raster',
            source: id,
            layout: { visibility: s.visible ? 'visible' : 'none' },
            paint: { 'raster-opacity': (s.opacity ?? 80) / 100 },
          }, anchor);
        } catch (e) {
          console.warn('Nie udało się dodać warstwy ' + w.code, e);
        }
      });
      // Kolejność: iterujemy DÓŁ→GÓRA i przesuwamy każdą tuż pod kotwicę overlayów → stack[0]
      // ląduje najwyżej z WMS, lecz wciąż POD granicami działek / otworami / bboxem.
      for (let i = stack.length - 1; i >= 0; i--) {
        const id = 'wms-' + stack[i].layer.code;
        if (m.getLayer(id)) { try { m.moveLayer(id, anchor); } catch { /* kotwica mogła chwilowo zniknąć */ } }
      }
    };
    // 'idle' (jak w efekcie PSH), nie loaded()/'load' — rastry potrafią trzymać loaded()=false.
    return gdyStylGotowy(m, apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSig, overlaySig]);

  // (b) MALUJĄCY: krycie + widoczność na żywo — setPaintProperty/setLayoutProperty, bez
  // dodawania/usuwania/przesuwania warstw i bez ponownego pobierania kafelków.
  const paintSig = effStack.map(s => `${s.layer.code}:${s.opacity}:${s.visible ? 1 : 0}`).join('|');
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const apply = () => stackRef.current.forEach(s => {
      const id = 'wms-' + s.layer.code;
      if (!m.getLayer(id)) return;
      m.setPaintProperty(id, 'raster-opacity', (s.opacity ?? 80) / 100);
      m.setLayoutProperty(id, 'visibility', s.visible ? 'visible' : 'none');
    });
    return gdyStylGotowy(m, apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paintSig]);

  // (b2) PODGLĄD GEOREFERENCERA: raster mapy jeszcze niezatwierdzonej. Osobne źródło o stałym id —
  // nie miesza się ze stosem warstw katalogu i znika razem z propsem.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const ID = 'georef-preview';
    const apply = () => {
      if (m.getLayer(ID)) m.removeLayer(ID);
      if (m.getSource(ID)) m.removeSource(ID);
      if (!previewRaster) return;
      try {
        m.addSource(ID, { type: 'raster', tiles: [previewRaster.url], tileSize: 256 });
        m.addLayer({
          id: ID,
          type: 'raster',
          source: ID,
          paint: { 'raster-opacity': previewRaster.opacity / 100 },
        }, overlayAnchor(m));
      } catch (e) {
        console.warn('Nie udało się dodać podglądu dopasowania', e);
      }
    };
    return gdyStylGotowy(m, apply);
  }, [previewRaster?.url, previewRaster?.opacity]);

  // (c) MARKERY: ujęcie / otwory (klucz [intake, markerMode]) — przewijanie listy warstw
  // ani zmiana kolejności nie odtwarzają już markerów DOM.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    const draw = () => {
      markersRef.current.forEach(mk => mk.remove());
      markersRef.current = [];

      // Główny punkt ujęcia — własne współrzędne (data.latitude/longitude). Większy, kolor "wody",
      // z ikoną kropli i etykietą poniżej. Renderowany przed markerami otworów żeby był widoczny.
      if (markerMode === 'intake' && intake?.latitude != null && intake?.longitude != null) {
        const wrap = document.createElement('div');
        wrap.style.cssText = 'display:flex;flex-direction:column;align-items:center;cursor:pointer';
        const pin = document.createElement('div');
        pin.style.cssText = 'width:28px;height:28px;border-radius:50% 50% 50% 0;background:#06B6D4;'
          + 'border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);transform:rotate(-45deg);'
          + 'display:flex;align-items:center;justify-content:center';
        const drop = document.createElement('div');
        // Kropla w środku pinu — biały SVG path, obrócony zgrubnie z rotacją pinu
        drop.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="white" '
          + 'style="transform:rotate(45deg)"><path d="M12 2s7 8 7 13a7 7 0 1 1-14 0c0-5 7-13 7-13z"/></svg>';
        pin.appendChild(drop);
        const label = document.createElement('div');
        label.textContent = intake.name ?? 'Ujęcie';
        label.style.cssText = 'margin-top:4px;padding:2px 6px;background:rgba(255,255,255,0.95);'
          + 'border:1px solid #06B6D4;border-radius:4px;font-size:11px;font-weight:600;'
          + 'color:#0a1224;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,0.15)';
        wrap.appendChild(pin);
        wrap.appendChild(label);
        const intakeMarker = new maplibregl.Marker({ element: wrap, anchor: 'bottom' })
          .setLngLat([Number(intake.longitude), Number(intake.latitude)])
          .setPopup(new maplibregl.Popup({ offset: 28 }).setHTML(
            `<strong>${intake.name ?? 'Ujęcie'}</strong>`
            + (intake.town ? `<br/>${intake.town}` : '')
            + (intake.qM3h ? `<br/>Q = ${intake.qM3h} m³/h` : '')
            + `<br/><span style="font-family:monospace;color:#666">${Number(intake.latitude).toFixed(5)}, ${Number(intake.longitude).toFixed(5)}</span>`
          ))
          .addTo(m);
        markersRef.current.push(intakeMarker);
      }

      if (markerMode === 'boreholes' && intake?.boreholes) {
        intake.boreholes.forEach(o => {
          if (o.longitude != null && o.latitude != null) {
            const wrap = document.createElement('div');
            wrap.style.cssText = 'display:flex;flex-direction:column;align-items:center;cursor:pointer';
            const pin = document.createElement('div');
            pin.style.cssText = 'width:22px;height:22px;border-radius:50% 50% 50% 0;background:#7c5cff;'
              + 'border:2.5px solid white;box-shadow:0 2px 5px rgba(0,0,0,0.3);transform:rotate(-45deg);'
              + 'display:flex;align-items:center;justify-content:center';
            const inner = document.createElement('div');
            inner.style.cssText = 'transform:rotate(45deg);color:white;font-size:9px;font-weight:700;font-family:monospace';
            inner.textContent = o.functionAbbreviation ?? '';
            pin.appendChild(inner);
            const label = document.createElement('div');
            label.textContent = o.designation;
            label.style.cssText = 'margin-top:3px;padding:1.5px 5px;background:rgba(255,255,255,0.95);'
              + 'border:1px solid #7c5cff;border-radius:3px;font-size:10px;font-weight:600;'
              + 'color:#0a1224;white-space:nowrap;box-shadow:0 1px 2px rgba(0,0,0,0.12)';
            wrap.appendChild(pin);
            wrap.appendChild(label);
            const bhMarker = new maplibregl.Marker({ element: wrap, anchor: 'bottom' })
              .setLngLat([Number(o.longitude), Number(o.latitude)])
              .setPopup(new maplibregl.Popup({ offset: 22 }).setHTML(
                `<strong>${o.designation}</strong><br/>${o.functionName}`
                + (o.totalDepth ? `<br/>Głębokość: ${o.totalDepth} m` : '')
                + (o.yieldM3h ? `<br/>Q = ${o.yieldM3h} m³/h` : '')
                + `<br/><span style="font-family:monospace;color:#666">${Number(o.latitude).toFixed(5)}, ${Number(o.longitude).toFixed(5)}</span>`
              ))
              .addTo(m);
            markersRef.current.push(bhMarker);
          }
        });
      }
    };
    return gdyStylGotowy(m, draw);
  }, [intake, markerMode]);

  // Auto-fly do współrzędnych ujęcia gdy zmieni się ujęcie. flyTo nie wymaga m.loaded() —
  // ustawia tylko docelowy stan kamery i mapa odtwarza animację gdy renderuje. Wcześniejsza
  // wersja z `m.once('load', fly)` była bugowata: po pierwszym 'load' event nigdy nie odpala
  // ponownie (nawet gdy m.loaded() przejściowo wraca do false podczas dograwania źródeł), więc
  // fly siedział w kolejce do nikąd.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    if (intake?.id == null || intake.latitude == null || intake.longitude == null) return;
    if (lastFlewIntakeIdRef.current === intake.id) return;
    lastFlewIntakeIdRef.current = intake.id;
    const lng = Number(intake.longitude);
    const lat = Number(intake.latitude);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    const targetZoom = Math.min(17, Math.max(13, m.getZoom()));
    try {
      m.flyTo({ center: [lng, lat], zoom: targetZoom, speed: 1.4, essential: true });
    } catch (e) {
      // Fallback: jumpTo (instant, bez animacji) gdy flyTo z jakiegoś powodu padło.
      console.warn('Auto-fly to intake failed, fallback to jumpTo:', e);
      try { m.jumpTo({ center: [lng, lat], zoom: targetZoom }); } catch { /* best effort */ }
    }
  }, [intake]);

  // Manual re-center triggered by parent (button "Centruj na ujęciu"). Only fires when the
  // trigger value actually changes, so plain re-renders don't recenter.
  useEffect(() => {
    if (centerTrigger === undefined) return;
    if (centerTrigger === lastCenterTriggerRef.current) return;
    lastCenterTriggerRef.current = centerTrigger;
    const m = mapRef.current;
    if (!m) return;
    if (intake?.latitude == null || intake.longitude == null) return;
    const lng = Number(intake.longitude);
    const lat = Number(intake.latitude);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      console.warn('Manual center: lat/lng nie są liczbami', { lat, lng });
      return;
    }
    try {
      // jumpTo NAJPIERW (gwarantowane) + flyTo dla animacji jako bonus.
      m.jumpTo({ center: [lng, lat], zoom: 15 });
      m.flyTo({ center: [lng, lat], zoom: 15, speed: 1.6, essential: true });
    } catch (e) {
      console.warn('Manual center failed:', e);
    }
  }, [centerTrigger, intake]);

  // Dolot do geometrii wskazanego obiektu (np. presja kliknięta na liście). Strażnik na nonce —
  // zwykły re-render nie rusza mapy, a ponowne wskazanie tego samego obiektu nadal działa.
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !focusGeometry) return;
    if (focusGeometry.nonce === lastFocusNonceRef.current) return;
    lastFocusNonceRef.current = focusGeometry.nonce;
    try {
      const geom = JSON.parse(focusGeometry.geoJson) as { coordinates?: unknown };
      let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
      const skan = (w: unknown): void => {
        if (!Array.isArray(w)) return;
        if (typeof w[0] === 'number' && typeof w[1] === 'number') {
          const [lng, lat] = w as number[];
          if (lng < minLng) minLng = lng; if (lng > maxLng) maxLng = lng;
          if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
          return;
        }
        for (const p of w) skan(p);
      };
      skan(geom.coordinates);
      if (!Number.isFinite(minLng) || !Number.isFinite(minLat)) return;
      // obiekt punktowy/zdegenerowany: fitBounds na zerowym prostokącie nie ustawia sensownego zoomu
      if (minLng === maxLng && minLat === maxLat) {
        m.flyTo({ center: [minLng, minLat], zoom: 16, essential: true });
        return;
      }
      // essential: bez tego MapLibre pomija animację przy prefers-reduced-motion i mapa nie drgnie
      m.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 60, maxZoom: 17, duration: 700, essential: true });
    } catch (e) {
      console.warn('Dolot do geometrii presji nie powiódł się:', e);
    }
  }, [focusGeometry]);

  // Wyszukiwarka miejscowości: przelot do wybranej lokalizacji + tymczasowy znacznik (różowy pin).
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    if (searchMarkerRef.current) { searchMarkerRef.current.remove(); searchMarkerRef.current = null; }
    if (!searchTarget) return;
    const { lng, lat, label } = searchTarget;
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    const wrap = document.createElement('div');
    wrap.style.cssText = 'display:flex;flex-direction:column;align-items:center;';
    const pin = document.createElement('div');
    pin.style.cssText = 'width:26px;height:26px;border-radius:50% 50% 50% 0;background:#e11d48;'
      + 'border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);transform:rotate(-45deg);'
      + 'display:flex;align-items:center;justify-content:center';
    const dot = document.createElement('div');
    dot.style.cssText = 'width:8px;height:8px;border-radius:50%;background:white;transform:rotate(45deg)';
    pin.appendChild(dot);
    const lbl = document.createElement('div');
    lbl.textContent = label;
    lbl.style.cssText = 'margin-top:4px;padding:2px 6px;background:rgba(255,255,255,0.95);'
      + 'border:1px solid #e11d48;border-radius:4px;font-size:11px;font-weight:600;color:#0a1224;'
      + 'white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,0.15)';
    wrap.appendChild(pin); wrap.appendChild(lbl);
    searchMarkerRef.current = new maplibregl.Marker({ element: wrap, anchor: 'bottom' })
      .setLngLat([lng, lat]).addTo(m);
    try {
      m.flyTo({ center: [lng, lat], zoom: Math.max(12, Math.min(m.getZoom(), 14)), speed: 1.4, essential: true });
    } catch {
      try { m.jumpTo({ center: [lng, lat], zoom: 13 }); } catch { /* best effort */ }
    }
  }, [searchTarget]);

  return <div ref={containerRef} style={{ width: '100%', height, borderRadius: 10 }} />;
}

function updateBboxLayer(m: MLMap, bbox: Bbox4326 | null) {
  const existing = m.getSource(BBOX_SOURCE);
  if (!bbox) {
    if (m.getLayer(BBOX_LINE)) m.removeLayer(BBOX_LINE);
    if (m.getLayer(BBOX_FILL)) m.removeLayer(BBOX_FILL);
    if (existing) m.removeSource(BBOX_SOURCE);
    return;
  }
  const data = bboxFeature(bbox);
  if (existing && 'setData' in existing) {
    (existing as maplibregl.GeoJSONSource).setData(data);
    return;
  }
  m.addSource(BBOX_SOURCE, { type: 'geojson', data });
  m.addLayer({
    id: BBOX_FILL,
    type: 'fill',
    source: BBOX_SOURCE,
    paint: { 'fill-color': '#d8354b', 'fill-opacity': 0.12 },
  });
  m.addLayer({
    id: BBOX_LINE,
    type: 'line',
    source: BBOX_SOURCE,
    paint: { 'line-color': '#d8354b', 'line-width': 2, 'line-dasharray': [3, 2] },
  });
}
